import { format, isSameDay, isValid, parse } from "date-fns";
import type { Locale } from "date-fns/locale";
import { resolveSupportedLocale } from "../utilities/localeCatalog";
import { getRelativeDateLabel, getRelativeDatePreview, isRelativeDateValue } from "../utilities/relativeDates";
import { defined } from "../utilities/typeHelper";
import type { ArtifactFilterRef } from "./protocol";

const ISO_DATE_LENGTH = 10;
const MAX_LISTED_VALUES = 3;

// The engine's date-times end in a literal "Z" on server wall-clock time, so parse the date part only.
const parseIsoDate = (value: string): Date | undefined => {
  const date = parse(value.slice(0, ISO_DATE_LENGTH), "yyyy-MM-dd", new Date());
  return isValid(date) ? date : undefined;
};

const parseIsoDates = (values: string[]): Date[] | undefined => {
  const dates: Date[] = [];
  for (const value of values) {
    const date = parseIsoDate(value);
    if (!date) {
      return undefined;
    }
    dates.push(date);
  }
  return dates;
};

const formatDay = (date: Date, locale: Locale): string => format(date, "PP", { locale });

const formatDayRange = (from: Date, to: Date, locale: Locale): string =>
  isSameDay(from, to) ? formatDay(from, locale) : `${formatDay(from, locale)} – ${formatDay(to, locale)}`;

const describeDateValue = (value: string, locale: Locale, now: Date): string => {
  if (isRelativeDateValue(value)) {
    return `${getRelativeDateLabel(value)} (${getRelativeDatePreview(value, now)})`;
  }
  const date = parseIsoDate(value);
  return date ? formatDay(date, locale) : value;
};

const describeDateValues = (values: string[], localeCode: string, now: Date): string => {
  const { locale } = resolveSupportedLocale(localeCode);
  if (values.length === 2) {
    const [from, to] = parseIsoDates(values) ?? [];
    if (from && to) {
      return formatDayRange(from, to, locale);
    }
    return `${describeDateValue(defined(values[0]), locale, now)} – ${describeDateValue(defined(values[1]), locale, now)}`;
  }
  return values.map((value) => describeDateValue(value, locale, now)).join(", ");
};

const describeListedValues = (values: string[], labels: Record<string, string>): string => {
  const names = values.map((value) => labels[value] ?? value);
  if (names.length <= MAX_LISTED_VALUES) {
    return names.join(", ");
  }
  return `${names.slice(0, MAX_LISTED_VALUES).join(", ")} and ${names.length - MAX_LISTED_VALUES} more`;
};

export const describeFilterValues = (
  filter: ArtifactFilterRef | undefined,
  values: string[],
  labels: Record<string, string>,
  localeCode: string,
  now: Date
): string => {
  if (!filter) {
    return "";
  }
  if (values.length === 0) {
    return "All";
  }
  return filter.kind === "Value" ? describeListedValues(values, labels) : describeDateValues(values, localeCode, now);
};
